import React from 'react';
import {FormattedMessage} from 'react-intl';
import Select, {GroupBase} from 'react-select';
import {AsyncProps, useAsync} from 'react-select/async';
// This import is necessary for module augmentation.
// It allows us to extend the 'Props' interface in the 'react-select/base' module
import type {} from 'react-select/base';
import {
  components,
  ClearIndicatorProps,
  ContainerProps,
  ControlProps,
  DropdownIndicatorProps,
  IndicatorsContainerProps,
  IndicatorSeparatorProps,
  InputProps,
  LoadingIndicatorProps,
  MenuProps,
  OptionProps,
  PlaceholderProps,
  ValueContainerProps
} from 'react-select';
import classNames from 'classnames';
import ButtonAction from '../ButtonAction';
import icon from '../../../assets/triangle-down.svg';
import clear from '../../../assets/icons/cancel-circle.svg';
import styles from './InputTypeAhead.module.scss';

/* eslint-disable */
declare module 'react-select/base' {
  export interface Props<
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>
  > {
    customClassnames?: ComponentCustomClassnames;
  }
}
/* eslint-disable */

type ComponentCustomClassnames = {
  selectContainer?: string
  valueContainer?: string
  indicatorsContainer?: string
  indicatorSeparator?: string
  dropdownIndicator?: string
  option?: string
  placeholder?: string
  menu?: string
  clearIndicator?: string
  input?: string
  loadingIndicator?: string
  control?: string
  label?: string
};

export interface InputTypeAheadProps<Option = unknown, IsMulti extends boolean = false, > extends AsyncProps<Option, IsMulti, any> {
  label?: React.ReactNode,
  error?: React.ReactNode,
  touched?: boolean,
}

const SelectContainer = <Option = unknown>({children, ...props}: ContainerProps<Option>) => (
  <components.SelectContainer
    {...props}
    className={classNames(styles.selectContainer, props.selectProps.customClassnames?.selectContainer)}
  >
    {children}
  </components.SelectContainer>
);

const Control = <Option = unknown>({children, ...props}: ControlProps<Option>) => (
  <components.Control
    {...props}
    className={classNames(styles.control, props.selectProps.customClassnames?.control, {[styles.disabled]: props.isDisabled})}
  >
    {children}
  </components.Control>
);

const ValueContainer = <Option = unknown>({
                                            children,
                                            ...props
                                          }: ValueContainerProps<Option>) => (
  <components.ValueContainer
    {...props}
    className={classNames(styles.valueContainer, props.selectProps.customClassnames?.valueContainer)}
  >
    {children}
  </components.ValueContainer>
);

const Option = <Option = unknown>({children, ...props}: OptionProps<Option>) => (
  <components.Option
    {...props}
    className={classNames(
      styles.option,
      {
        [styles.focused]: props.isFocused,
        [styles.selected]: props.isSelected,
      },
      props.selectProps.customClassnames?.option
    )}
  >
    {children}
  </components.Option>
);

const IndicatorSeparator = <Option = unknown>({...props}: IndicatorSeparatorProps<Option>) => (
  <components.IndicatorSeparator
    {...props}
    className={classNames(styles.indicatorSeparator, props.selectProps.customClassnames?.indicatorSeparator)}
  />
);

const IndicatorsContainer = <Option = unknown>({
                                                 children,
                                                 ...props
                                               }: IndicatorsContainerProps<Option>) => (
  <components.IndicatorsContainer
    {...props}
    className={classNames(styles.indicatorContainer, props.selectProps.customClassnames?.indicatorsContainer)}
  >
    {children}
  </components.IndicatorsContainer>
);

const DropdownIndicator = <Option = unknown>({...props}: DropdownIndicatorProps<Option>) => (
  <components.DropdownIndicator
    {...props}
    className={classNames(styles.ddIndicator, props.selectProps.customClassnames?.dropdownIndicator)}
  >
    <img src={icon} alt="triangle-down"/>
  </components.DropdownIndicator>
);

const Placeholder = <Option = unknown>({children, ...props}: PlaceholderProps<Option>) => (
  <components.Placeholder
    {...props}
    className={classNames(styles.placeholder, props.selectProps.customClassnames?.placeholder)}
  >
    {children}
  </components.Placeholder>
);

const Menu = <Option = unknown>({children, ...props}: MenuProps<Option>) => (
  <components.Menu
    {...props}
    className={classNames(styles.menu, props.selectProps.customClassnames?.menu)}
  >
    {children}
  </components.Menu>
);

const ClearIndicator = <Option = unknown>({...props}: ClearIndicatorProps<Option>) => (
  <components.ClearIndicator
    {...props}
    className={classNames(styles.clearIndicator, props.selectProps.customClassnames?.clearIndicator)}
  >
    <ButtonAction
      variant={'icon'}
      icon={clear}
      customClassnames={{container: styles.btnReset}}
    />
  </components.ClearIndicator>
);

const Input = <Option = unknown>({children, ...props}: InputProps<Option>) => (
  <components.Input
    {...props}
    className={classNames(styles.input, props.selectProps.customClassnames?.input)}
  >
    {children}
  </components.Input>
);

const LoadingIndicator = <Option = unknown>({...props}: LoadingIndicatorProps<Option>) => (
  <components.LoadingIndicator
    {...props}
    className={classNames(styles.loadingIndicator, props.selectProps.customClassnames?.loadingIndicator)}
  />
);

const InputTypeAhead = <OptionType, IsMulti extends boolean = false>({
                                                                       label,
                                                                       touched,
                                                                       error,
                                                                       value,
                                                                       customClassnames,
                                                                       noOptionsMessage = () => <FormattedMessage id={'message-no-options'} defaultMessage={'No options'}/>,
                                                                       ...rest
                                                                     }: InputTypeAheadProps<OptionType, IsMulti>) => {
  const showErrorCondition = touched && error && !value;

  const selectProps = useAsync({
    ...rest,
    isSearchable: true,
    isClearable: true,
    isLoading: false,
    backspaceRemovesValue: true,
    cacheOptions: true,
    defaultOptions: true,
    value: value,
    customClassnames,
    noOptionsMessage,
    components: {
      SelectContainer,
      Control,
      ValueContainer,
      IndicatorsContainer,
      IndicatorSeparator,
      DropdownIndicator,
      Option,
      Placeholder,
      Menu,
      ClearIndicator,
      Input,
      LoadingIndicator
    }
  });

  return (
    <div className={styles.container}>
      <Select<OptionType, IsMulti, any> {...selectProps}/>
      <div className={classNames(styles.label, customClassnames?.label)}>
        {label}
      </div>
      {showErrorCondition && (
        <div className={styles.errorContainer}>
          <div className={styles.error}>{error}</div>
        </div>
      )}
    </div>
  );
};

export default InputTypeAhead;
